import * as React from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 5198:982 — Sidebar Components):
 *
 * Advanced sidebar sub-components:
 *   SidebarMenuSub, SidebarMenuSubItem, SidebarGroupAction,
 *   SidebarMediaAsset, SidebarHeaderAction, SidebarPopoverTrigger,
 *   SidebarPopoverMenu, SidebarPopoverMenuItem, SidebarInboxItem
 */

/* ─── SidebarMenuSub ─── */

export type SidebarMenuSubStyle = 'default' | 'border' | 'indent'

export interface IntuiSidebarMenuSubProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Sub-menu visual style */
  variant?: SidebarMenuSubStyle
}

const IntuiSidebarMenuSub = React.forwardRef<HTMLDivElement, IntuiSidebarMenuSubProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col',
        variant === 'border' && 'ml-3.5 border-l border-border pl-2',
        variant === 'indent' && 'ml-6',
        variant === 'default' && 'ml-4',
        className,
      )}
      {...props}
    />
  ),
)
IntuiSidebarMenuSub.displayName = 'IntuiSidebarMenuSub'

/* ─── SidebarMenuSubItem ─── */

export interface IntuiSidebarMenuSubItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean
}

const IntuiSidebarMenuSubItem = React.forwardRef<HTMLButtonElement, IntuiSidebarMenuSubItemProps>(
  ({ className, isActive = false, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      data-active={isActive || undefined}
      className={cn(
        'flex h-7 w-full items-center rounded-md px-2 text-sm leading-none text-left',
        'text-foreground',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        isActive && 'bg-accent text-accent-foreground font-medium',
        className,
      )}
      {...props}
    >
      <span className="truncate">{children}</span>
    </button>
  ),
)
IntuiSidebarMenuSubItem.displayName = 'IntuiSidebarMenuSubItem'

/* ─── SidebarGroupAction ─── */

/** Icon-only button — always provide `aria-label` for accessibility */
const IntuiSidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { 'aria-label'?: string }
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'inline-flex size-5 shrink-0 items-center justify-center rounded-md',
      'text-muted-foreground',
      'hover:bg-accent hover:text-accent-foreground',
      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
      className,
    )}
    {...props}
  />
))
IntuiSidebarGroupAction.displayName = 'IntuiSidebarGroupAction'

/* ─── SidebarMediaAsset ─── */

export interface IntuiSidebarMediaAssetProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Type of media: icon (dark bg) or avatar (image) */
  type?: 'icon' | 'avatar'
}

const IntuiSidebarMediaAsset = React.forwardRef<HTMLDivElement, IntuiSidebarMediaAssetProps>(
  ({ className, type = 'icon', children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg size-8',
        type === 'icon' && 'bg-primary text-primary-foreground [&>svg]:size-4',
        type === 'avatar' && 'overflow-hidden',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
)
IntuiSidebarMediaAsset.displayName = 'IntuiSidebarMediaAsset'

/* ─── SidebarHeaderAction ─── */

/** Icon-only button — always provide `aria-label` for accessibility */
const IntuiSidebarHeaderAction = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { 'aria-label'?: string }
>(({ className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'inline-flex size-7 shrink-0 items-center justify-center rounded-md',
      'text-foreground',
      'hover:bg-accent hover:text-accent-foreground',
      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
      '[&>svg]:size-4',
      className,
    )}
    {...props}
  />
))
IntuiSidebarHeaderAction.displayName = 'IntuiSidebarHeaderAction'

/* ─── SidebarPopoverTrigger ─── */

export interface IntuiSidebarPopoverTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode
  title: string
  badge?: React.ReactNode
}

const IntuiSidebarPopoverTrigger = React.forwardRef<HTMLButtonElement, IntuiSidebarPopoverTriggerProps>(
  ({ className, icon, title, badge, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex h-9 w-full items-center gap-2 rounded-md px-2',
        'text-sm text-foreground',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        className,
      )}
      {...props}
    >
      {icon && <span className="flex shrink-0 items-center justify-center [&>svg]:size-4">{icon}</span>}
      <span className="flex-1 truncate text-left font-medium">{title}</span>
      {badge && <span className="shrink-0">{badge}</span>}
    </button>
  ),
)
IntuiSidebarPopoverTrigger.displayName = 'IntuiSidebarPopoverTrigger'

/* ─── SidebarPopoverMenu ─── */

const IntuiSidebarPopoverMenu = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex flex-col rounded-lg border bg-popover p-1 shadow-md',
      'text-popover-foreground',
      className,
    )}
    {...props}
  />
))
IntuiSidebarPopoverMenu.displayName = 'IntuiSidebarPopoverMenu'

/* ─── SidebarPopoverMenuItem ─── */

export interface IntuiSidebarPopoverMenuItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode
  isSelected?: boolean
}

const IntuiSidebarPopoverMenuItem = React.forwardRef<HTMLButtonElement, IntuiSidebarPopoverMenuItemProps>(
  ({ className, icon, isSelected = false, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex h-10 w-full items-center gap-2 rounded-md px-2 text-sm text-left',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        className,
      )}
      {...props}
    >
      {icon && <span className="flex shrink-0 items-center justify-center size-5 [&>svg]:size-4">{icon}</span>}
      <span className="flex-1 truncate">{children}</span>
      {isSelected && <Check className="size-4 shrink-0" />}
    </button>
  ),
)
IntuiSidebarPopoverMenuItem.displayName = 'IntuiSidebarPopoverMenuItem'

/* ─── SidebarInboxItem ─── */

export interface IntuiSidebarInboxItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  sender: string
  subject: string
  preview?: string
  timestamp?: string
}

const IntuiSidebarInboxItem = React.forwardRef<HTMLButtonElement, IntuiSidebarInboxItemProps>(
  ({ className, sender, subject, preview, timestamp, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        'flex w-full flex-col gap-1 rounded-md p-3 text-left',
        'hover:bg-accent',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        className,
      )}
      {...props}
    >
      <div className="flex items-center justify-between">
        <span className="min-w-0 flex-1 text-sm font-medium text-foreground truncate">{sender}</span>
        {timestamp && <span className="text-xs text-muted-foreground/70 shrink-0">{timestamp}</span>}
      </div>
      <span className="text-sm font-medium text-foreground truncate">{subject}</span>
      {preview && (
        <span className="text-xs text-muted-foreground line-clamp-2">{preview}</span>
      )}
    </button>
  ),
)
IntuiSidebarInboxItem.displayName = 'IntuiSidebarInboxItem'

export {
  IntuiSidebarMenuSub,
  IntuiSidebarMenuSubItem,
  IntuiSidebarGroupAction,
  IntuiSidebarMediaAsset,
  IntuiSidebarHeaderAction,
  IntuiSidebarPopoverTrigger,
  IntuiSidebarPopoverMenu,
  IntuiSidebarPopoverMenuItem,
  IntuiSidebarInboxItem,
}
